/**
 * Modal — Fixed overlay (rgba 16,24,40, 0.42), grid center.
 *
 * Container: bg card, radius r4, shadow-lg, width 520
 *   size="sm"   → 380
 *   size="wide" → 760
 *   size="full" → 96vw / 100% width
 *   max-height 90vh / 90%, flex column
 *
 * Head: padding 14 18, border-bottom line, flex space-between
 *   → h2 lg semibold + close ghost icon button
 * Body: padding 18, overflow-y auto, flex 1
 * Foot: padding 12 18, border-top line, bg paper2, flex end gap 8
 *
 * Behaviour: close on Escape + backdrop click
 * Animation: slideup (translateY 12 → 0, opacity 0 → 1, 180 ms)
 */

import React, { useEffect, useRef } from 'react';
import {
  Modal as RNModal,
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TouchableWithoutFeedback,
  Animated,
  ViewStyle,
  StyleProp,
  Platform,
} from 'react-native';
import { useTheme } from '../context/themeContext';

// ── Tiny inline icon paths (subset) ─────────────────────────
const X_PATH =
  'M6 6l12 12M18 6 6 18';

// Simple SVG-based close icon for web, fallback text for native
function CloseIcon({ color, size = 14 }: { color: string; size?: number }) {
  if (Platform.OS === 'web') {
    return (
      // @ts-ignore — web only
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d={X_PATH} />
      </svg>
    );
  }
  return (
    <Text style={{ fontSize: size, color, lineHeight: size * 1.2 }}>✕</Text>
  );
}

export type ModalSize = 'sm' | 'wide' | 'full' | undefined;

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  footer?: React.ReactNode;
  children?: React.ReactNode;
  size?: ModalSize;
  // Edge-to-edge page: fills the viewport, no rounded card, no dimmed backdrop.
  // Use for focused create/edit flows that should read as their own screen.
  fullScreen?: boolean;
  closeOnBackdrop?: boolean;
  style?: StyleProp<ViewStyle>;
  // Rendered absolutely over the WHOLE dialog (above head/body/footer, no
  // scroll) — e.g. an in-modal confirmation that must cover the full card.
  overlay?: React.ReactNode;
}

export function Modal({
  open,
  onClose,
  title,
  footer,
  children,
  size,
  fullScreen = false,
  closeOnBackdrop = true,
  style,
  overlay,
}: ModalProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, spacing, shadows } = theme;

  // ── Slide-up animation ───────────────────────────────────
  const translateY = useRef(new Animated.Value(12)).current;
  const opacity    = useRef(new Animated.Value(0)).current;
  // A touch of scale on top of the rise — the card reads as coming forward
  // rather than sliding, which is what makes the entrance feel expensive. Kept
  // to 200ms so it never delays interaction.
  const scale      = useRef(new Animated.Value(0.975)).current;

  useEffect(() => {
    if (open) {
      translateY.setValue(12);
      opacity.setValue(0);
      scale.setValue(0.975);
      Animated.parallel([
        Animated.spring(translateY, {
          toValue: 0,
          damping: 22,
          stiffness: 320,
          mass: 0.7,
          useNativeDriver: true,
        }),
        Animated.timing(opacity, {
          toValue: 1,
          duration: 160,
          useNativeDriver: true,
        }),
        Animated.spring(scale, {
          toValue: 1,
          damping: 22,
          stiffness: 320,
          mass: 0.7,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [open, translateY, opacity, scale]);

  // ── Escape key (web) ─────────────────────────────────────
  useEffect(() => {
    if (!open || Platform.OS !== 'web') return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // ── Container width by size ──────────────────────────────
  const containerWidth: ViewStyle =
    size === 'sm'   ? { width: 380 }      :
    size === 'wide' ? { width: 760 }      :
    size === 'full' ? { width: '96%' }    :
    /* default */     { width: 520 };

  if (!open) return null;

  return (
    <RNModal
      visible={open}
      transparent
      animationType="none"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      {/* Backdrop */}
      <TouchableWithoutFeedback
        onPress={!fullScreen && closeOnBackdrop ? onClose : undefined}
        accessible={false}
      >
        <View
          style={{
            flex: 1,
            backgroundColor: fullScreen ? colors.paper : 'rgba(16,24,40,0.55)',
            alignItems: 'center',
            justifyContent: 'center',
            padding: fullScreen ? 0 : spacing[6],
            // Frosted backdrop — a premium focus cue (web only).
            ...(Platform.OS === 'web' && !fullScreen ? ({ backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' } as any) : {}),
          }}
        >
          {/* Stop tap propagation from dialog to backdrop */}
          <TouchableWithoutFeedback accessible={false}>
            <Animated.View
              style={[
                {
                  backgroundColor: colors.card,
                  borderRadius: fullScreen ? 0 : 18,
                  borderWidth: fullScreen ? 0 : 1,
                  borderColor: colors.line,
                  maxHeight: fullScreen ? '100%' : '90%',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  maxWidth: '100%',
                },
                fullScreen ? { width: '100%', height: '100%', flex: 1 } : containerWidth,
                fullScreen ? null : (shadows.lg as ViewStyle),
                { transform: [{ translateY }, { scale }], opacity },
                style,
              ]}
              accessibilityViewIsModal
              // @ts-ignore — web role
              role="dialog"
              {...({ "aria-modal": true } as any)}
            >
              {/* ── Head ─────────────────────────────────── */}
              {title ? (
                <View
                  style={{
                    paddingVertical: fullScreen ? 16 : 14,
                    paddingHorizontal: fullScreen ? 0 : 18,
                    borderBottomWidth: 1,
                    borderBottomColor: colors.line,
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  {/* Centred rail keeps the title aligned with the body columns. */}
                  <View
                    style={{
                      flex: 1,
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: spacing[2],
                      ...(fullScreen ? { width: '100%', maxWidth: 1500, alignSelf: 'center', paddingHorizontal: 24 } : null),
                    }}
                  >
                    {/* Title with a glowing accent rail */}
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 11, flex: 1, minWidth: 0 }}>
                      <View
                        style={{
                          width: 4, height: 20, borderRadius: 4, backgroundColor: colors.accent, flexShrink: 0,
                          ...(Platform.OS === 'web' ? ({ backgroundImage: `linear-gradient(180deg, ${colors.accent}, ${colors.accentInk})`, boxShadow: `0 2px 8px -2px ${colors.accent}80` } as any) : {}),
                        }}
                      />
                      <Text
                        style={{
                          margin: 0,
                          fontSize: fontSize.xl,
                          fontWeight: fontWeight.bold,
                          color: colors.ink,
                          fontFamily: theme.font.sans,
                          letterSpacing: -0.3,
                          flex: 1,
                        }}
                        numberOfLines={1}
                      >
                        {title}
                      </Text>
                    </View>

                    {/* Ghost close button */}
                    <TouchableOpacity
                      onPress={onClose}
                      accessibilityLabel="Close"
                      style={{
                        width: 30,
                        height: 30,
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 8,
                        borderWidth: 1,
                        borderColor: colors.line,
                        backgroundColor: colors.paper2,
                        ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : {}),
                      }}
                      hitSlop={{ top: 6, right: 6, bottom: 6, left: 6 }}
                    >
                      <CloseIcon color={colors.ink2} size={14} />
                    </TouchableOpacity>
                  </View>
                </View>
              ) : null}

              {/* ── Body ─────────────────────────────────── */}
              <ScrollView
                style={{ flex: 1 }}
                contentContainerStyle={
                  fullScreen
                    ? { paddingHorizontal: 24, paddingVertical: 20, width: '100%', maxWidth: 1500, alignSelf: 'center' }
                    : { padding: 18 }
                }
                showsVerticalScrollIndicator
                keyboardShouldPersistTaps="handled"
              >
                {children}
              </ScrollView>

              {/* ── Footer ───────────────────────────────── */}
              {footer ? (
                <View
                  style={{
                    paddingVertical: spacing[3],
                    paddingHorizontal: fullScreen ? 0 : 18,
                    borderTopWidth: 1,
                    borderTopColor: colors.line,
                    backgroundColor: colors.paper2,
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: spacing[2],
                  }}
                >
                  {fullScreen ? (
                    // Same centred rail as the body so the actions line up with
                    // the column edges instead of the screen edges.
                    <View
                      style={{
                        flex: 1,
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: spacing[2],
                        width: '100%',
                        maxWidth: 1500,
                        alignSelf: 'center',
                        paddingHorizontal: 24,
                      }}
                    >
                      {footer}
                    </View>
                  ) : (
                    footer
                  )}
                </View>
              ) : null}

              {/* Full-dialog overlay (confirmations etc.) — covers head/body/
                  footer, doesn't scroll. */}
              {overlay}
            </Animated.View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </RNModal>
  );
}

export default Modal;
